import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { islandLabel } from "@/lib/islands";

function Delta({ now, prev }) {
  const up = now > prev, down = now < prev;
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  const cls = up ? "text-emeraude-dark" : down ? "text-red-500" : "text-muted2";
  const pct = prev > 0 ? Math.round(((now - prev) / prev) * 100) : (now > 0 ? 100 : 0);
  return <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${cls}`}><Icon className="w-3.5 h-3.5" />{Math.abs(pct)}%</span>;
}

export function OverviewPanel({ data, onPeriod }) {
  const { t } = useTranslation();
  const [months, setMonths] = useState(6);
  const [year, setYear] = useState(0);
  const nowY = new Date().getFullYear();
  const d = data || {};
  const cmp = d.comparison;
  const setP = (n) => { setMonths(n); onPeriod?.(n, year); };
  const exportCsv = () => {
    const rows = [["month", "bookings", "revenue"], ...(d.months || []).map((m) => [m.month, m.bookings, m.revenue])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "caribbean-drive-stats.csv";
    a.click();
  };
  const islandData = (d.by_island || []).map((x) => ({ ...x, name: islandLabel(x.island) }));

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {cmp && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-card border border-border p-5" data-testid="ov-cmp-bookings">
            <p className="text-xs text-muted2 font-semibold uppercase">{t("ov.bookings")} · {t("ov.thisMonth")}</p>
            <div className="flex items-end gap-2 mt-1"><p className="text-3xl font-heading font-extrabold text-ink">{cmp.bookings_this}</p><span className="mb-1"><Delta now={cmp.bookings_this} prev={cmp.bookings_last} /></span></div>
            <p className="text-xs text-muted2">{t("ov.vsLast")}: {cmp.bookings_last}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-card border border-border p-5" data-testid="ov-cmp-revenue">
            <p className="text-xs text-muted2 font-semibold uppercase">{t("ov.revenue")} · {t("ov.thisMonth")}</p>
            <div className="flex items-end gap-2 mt-1"><p className="text-3xl font-heading font-extrabold text-ink">{cmp.revenue_this} €</p><span className="mb-1"><Delta now={cmp.revenue_this} prev={cmp.revenue_last} /></span></div>
            <p className="text-xs text-muted2">{t("ov.vsLast")}: {cmp.revenue_last} €</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-card border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg text-ink">{t("ov.title")}</h3>
          <div className="flex items-center gap-2">
            <select data-testid="ov-year" value={year} onChange={(e) => { const y = Number(e.target.value); setYear(y); onPeriod?.(months, y); }} className="px-2 py-1 rounded-full text-xs font-bold bg-sand text-ink border border-border">
              <option value={0}>{t("ov.yearRolling")}</option>
              {[nowY, nowY - 1, nowY - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button data-testid="ov-export-csv" onClick={exportCsv} className="px-3 py-1 rounded-full text-xs font-bold bg-emeraude/10 text-emeraude-dark hover:bg-emeraude/20">{t("ov.export")}</button>
            <div className="flex gap-1 bg-sand rounded-full p-1">
              {[6, 12].map((n) => <button key={n} data-testid={`ov-period-${n}`} onClick={() => setP(n)} className={`px-3 py-1 rounded-full text-xs font-bold ${months === n ? "bg-canard text-white" : "text-muted2"}`}>{t(n === 6 ? "ov.period6" : "ov.period12")}</button>)}
            </div>
          </div>
        </div>
        {(d.months || []).length === 0 ? <p className="text-center py-8 text-muted2 font-body">{t("ov.noData")}</p> : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={d.months} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6ede9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" tick={{ fontSize: 12, fill: "#4B6B6B" }} allowDecimals={false} axisLine={false} tickLine={false} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 12, fill: "#0E8F7E" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e6ede9", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Bar yAxisId="l" dataKey="bookings" name={t("ov.bookings")} fill="#0E7C86" radius={[6, 6, 0, 0]} maxBarSize={40} />
              <Line yAxisId="r" type="monotone" dataKey="revenue" name={t("ov.revenue")} stroke="#0E8F7E" strokeWidth={3} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-border p-6" data-testid="ov-by-island">
        <h3 className="font-heading font-bold text-lg text-ink mb-4">{t("ov.byIsland")}</h3>
        {islandData.length === 0 ? <p className="text-center py-8 text-muted2 font-body">{t("ov.noData")}</p> : (
          <ResponsiveContainer width="100%" height={Math.max(160, islandData.length * 44)}>
            <BarChart data={islandData} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6ede9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e6ede9", fontSize: 13 }} />
              <Bar dataKey="revenue" name={t("ov.revenue")} fill="#0E7C86" radius={[0, 6, 6, 0]} maxBarSize={26} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {(d.by_loueur || []).length > 0 && (
        <div className="bg-white rounded-2xl shadow-card border border-border p-6" data-testid="ov-by-loueur">
          <h3 className="font-heading font-bold text-lg text-ink mb-4">{t("ov.topLoueurs")}</h3>
          <div className="space-y-2">
            {d.by_loueur.map((l, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-border/50 pb-2 last:border-0">
                <span className="font-semibold text-ink">{i + 1}. {l.loueur}</span>
                <span className="font-heading font-bold text-canard-dark">{l.revenue} €</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
